import React, { useState, useContext, useEffect } from "react";
import { useUsers } from "../../contexts/UsersContext";
import { RoleContext } from "../../contexts/RoleContext";
import { AuthContext } from "../../contexts/AuthContext";
import { useRoleData } from "../../contexts/RoleDataContext";

const UserForm = ({ closeModal, onSubmit, initialValues }) => {
  const { users } = useUsers(); // Fetch users from context
  const roleContext = useContext(RoleContext);
  const { token } = useContext(AuthContext);
  const { getManagerUsers } = useRoleData(); // Get manager users from RoleDataContext
  const roles = roleContext?.roles || [];
  const loadRoles = roleContext?.loadRoles;
  const managerUsers = getManagerUsers(); // Get list of manager users
  // Initialize formData with proper role handling
  const [formData, setFormData] = useState(() => {
    if (!initialValues) {
      return {
        name: "",
        email: "",
        role: "",
        manager_id: "", 
        password: "",
        password_confirmation: "",
      };
    }
    
    // Get role from initialValues - handle both role_id, role and roles array
    let roleId = "";
    if (initialValues.role) {
      roleId = initialValues.role;
    } else if (initialValues.role_id) {
      roleId = initialValues.role_id;
    } else if (initialValues.roles && initialValues.roles.length > 0) {
      roleId = initialValues.roles[0].id || "";
    }
    
    return {
      name: initialValues.name || "",
      email: initialValues.email || "",
      role: roleId,
      manager_id: initialValues.manager_id || "", 
      password: "",
      password_confirmation: "",
    };
  });

  // Field errors state
  const [fieldErrors, setFieldErrors] = useState({
    name: "",
    email: "",
    role: "",
    password: "",
    password_confirmation: "",
  });

  // Load roles when component mounts
  useEffect(() => {
    if (token && loadRoles && typeof loadRoles === 'function') {
      loadRoles(token);
    }
  }, [token, loadRoles]);

  // Validation functions
  const validateName = (value) => {
    if (!value || value.trim().length === 0) {
      return "";
    }
    if (value.length > 100) {
      return "Name must not exceed 100 characters.";
    }
    if (value.trim().length > 0 && value.trim().length < 2) {
      return "Name must be at least 2 characters long.";
    }
    if (value.trim().length > 0 && !/^[A-Za-z]+(?:\s[A-Za-z]+)*$/.test(value.trim())) {
      return "Name must contain only letters and spaces in between words (no leading or trailing spaces).";
    }
    return "";
  };

  const validateEmail = (value) => {
    if (!value || value.trim().length === 0) {
      return "";
    }
    if (value.length > 255) {
      return "Email must not exceed 255 characters.";
    }
    if (value.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
      return "Invalid email format.";
    }
    return "";
  };

  const validatePassword = (value) => {
    if (!value || value.length === 0) {
      return "";
    }
    if (value.length > 128) {
      return "Password must not exceed 128 characters.";
    }
    if (value.length > 0 && value.length < 8) {
      return "Password must be at least 8 characters long.";
    }
    if (value.length > 0 && !/^(?=.*[A-Z]).{8,}$/.test(value)) {
      return "Password must contain at least one uppercase letter.";
    }
    return "";
  };

  const validatePasswordConfirmation = (value, password) => {
    if (!value || value.length === 0) {
      return "";
    }
    if (value.length > 128) {
      return "Password confirmation must not exceed 128 characters.";
    }
    if (value !== password) {
      return "Passwords do not match.";
    }
    return "";
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Real-time validation
    let error = "";
    switch (name) {
      case "name":
        error = validateName(value);
        break;
      case "email":
        error = validateEmail(value);
        break;
      case "role":
        error = !value || value === "" ? "Role is required. Please select a role." : "";
        break;
      case "password":
        error = validatePassword(value);
        setFieldErrors((prev) => ({
          ...prev,
          password: error,
          // Also validate password_confirmation if it exists
          password_confirmation: formData.password_confirmation 
            ? validatePasswordConfirmation(formData.password_confirmation, value)
            : prev.password_confirmation,
        }));
        return; // Early return to handle both password errors
      case "password_confirmation":
        error = validatePasswordConfirmation(value, formData.password);
        break;
      default:
        break;
    }

    setFieldErrors((prev) => ({
      ...prev,
      [name]: error,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate all fields and update errors
    const nameError = !formData.name || formData.name.trim().length === 0 
      ? "Name is required." 
      : validateName(formData.name);
    const emailError = !formData.email || formData.email.trim().length === 0 
      ? "Email is required." 
      : validateEmail(formData.email);
    
    let passwordError = "";
    let passwordConfirmationError = "";
    const isEditing = Boolean(initialValues);
    const isPasswordEntered = formData.password || formData.password_confirmation;

    if (!isEditing || isPasswordEntered) {
      passwordError = !formData.password || formData.password.length === 0 
        ? "Password is required." 
        : validatePassword(formData.password);
      passwordConfirmationError = !formData.password_confirmation || formData.password_confirmation.length === 0 
        ? "Password confirmation is required." 
        : validatePasswordConfirmation(formData.password_confirmation, formData.password);
    }

    // Role validation: required
    const roleError = !formData.role || formData.role === "" ? "Role is required. Please select a role." : "";

    // Update all field errors
    setFieldErrors({
      name: nameError,
      email: emailError,
      role: roleError,
      password: passwordError,
      password_confirmation: passwordConfirmationError,
    });

    // Check if there are any validation errors
    if (nameError || emailError || roleError || passwordError || passwordConfirmationError) {
      return; // Don't submit if there are errors
    }

    // Manager validation: if selected, must be a valid manager ID
    if (formData.manager_id && formData.manager_id !== "") {
      const managerExists = managerUsers.some(manager => manager.id === parseInt(formData.manager_id));
      if (!managerExists) {
        alert("Selected manager is invalid.");
        return;
      }
    }
    
    // Convert role ID to role name
    const selectedRole = roles.find(r => r.id === parseInt(formData.role));
    const roleName = selectedRole ? selectedRole.name : formData.role;
    
    // Trim string fields before submission
    const trimmedFormData = {
      ...formData,
      name: formData.name.trim(),
      email: formData.email.trim(),
      role: roleName, // Send role name instead of ID
    };
    
    onSubmit(trimmedFormData); // Pass the form data to the parent component
    // closeModal(); // Close the modal
  };


  return (
    <form onSubmit={handleSubmit} autoComplete="off" className="space-y-4 font-poppins">
      {/* Full Name Field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-white">
          Full Name <span className='text-red-500'>*</span>
        </label>
        <input
          type="text"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          className={`mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border rounded-md ${
            fieldErrors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          required
          minLength={2}
        />
        {fieldErrors.name && (
          <p className="mt-1 text-sm text-red-500">{fieldErrors.name}</p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-white">
          Email <span className='text-red-500'>*</span>
        </label>
        <input
          type="email"
          name="email"
          id="email"
          autoComplete="off"
          value={formData.email}
          onChange={handleChange}
          className={`mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border rounded-md ${
            fieldErrors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        />
        {fieldErrors.email && (
          <p className="mt-1 text-sm text-red-500">{fieldErrors.email}</p>
        )}
      </div>

      {/* Role Dropdown */}
      <div>
        <label htmlFor="role" className="block text-sm font-medium text-gray-700 dark:text-white">
          Role <span className='text-red-500'>*</span>
        </label>
        <select
          name="role"
          id="role"
          value={formData.role}
          onChange={handleChange}
          className={`mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border rounded-md ${
            fieldErrors.role ? 'border-red-500' : 'border-gray-300'
          }`}
          required
        >
          <option value="">Select a role</option>
          {roles && roles.length > 0 ? (
            roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))
          ) : (
            <option value="" disabled>Loading roles...</option>
          )}
        </select>
        {fieldErrors.role && (
          <p className="mt-1 text-sm text-red-500">{fieldErrors.role}</p>
        )}
      </div>

      {/* Manager Dropdown */}
      <div>
        <label htmlFor="manager_id" className="block text-sm font-medium text-gray-700 dark:text-white">
          Manager 
        </label>
        <select
          name="manager_id"
          id="manager_id"
          value={formData.manager_id}
          onChange={handleChange}
          className="mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          <option value="">Select a manager</option>
          {managerUsers && managerUsers.length > 0 ? (
            managerUsers.map((manager) => (
              <option key={manager.id} value={manager.id}>
                {manager.name}
              </option>
            ))
          ) : (
            <option value="" disabled>No managers available</option>
          )}
        </select>
      </div>

      {/* Password Fields - Only for Adding New Users */}
      {!initialValues && (
        <>
          <div>
            <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-white">
              Password <span className='text-red-500'>*</span>
            </label>
            <input
              type="password"
              name="password"
              id="password"
              autoComplete="new-password"
              value={formData.password}
              onChange={handleChange}
              className={`mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border rounded-md ${
                fieldErrors.password ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={8}
            />
            {fieldErrors.password && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.password}</p>
            )}
          </div>

          <div>
            <label htmlFor="password_confirmation" className="block text-sm font-medium text-gray-700 dark:text-white">
              Confirm Password <span className='text-red-500'>*</span>
            </label>
            <input
              type="password"
              name="password_confirmation"
              id="password_confirmation"
              value={formData.password_confirmation}
              onChange={handleChange}
              className={`mt-1 block w-full p-2 dark:bg-black dark:text-white dark:focus:outline-none border rounded-md ${
                fieldErrors.password_confirmation ? 'border-red-500' : 'border-gray-300'
              }`}
              required
              minLength={8}
            />
            {fieldErrors.password_confirmation && (
              <p className="mt-1 text-sm text-red-500">{fieldErrors.password_confirmation}</p>
            )}
          </div>
        </>
      )}

      {/* Action Buttons */}
      <div className="flex justify-end items-center gap-2">
        <button
          type="button"
          onClick={closeModal}
          className="mt-1 px-8 py-2 hover:bg-gray-200 font-medium dark:hover:bg-gray-900 dark:bg-black dark:text-white dark:focus:outline-none border border-gray-300 rounded-md"
        >
          Cancel
        </button>
        <button
          type="submit"
          className="px-8 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
        >
          {initialValues ? "Update" : "Add User"}
        </button>
      </div>
    </form>
  );
};

export default UserForm;
